import { translatePlural } from 'aos-helpers/src/helpers/translations/Translations'
import {
    InspectionItemPayload,
    InspectionTaskFormPayload,
} from 'aos-services/src/services/tasks/types/payload/InspectionTaskFormPayload'
import {
    InspectionGroup,
    translateInspectionGroup,
} from 'aos-services/src/services/tasks/types/task/InspectionGroup'
import {
    diffSelection,
    inspectionTypeToGroups,
} from 'aos-services/src/services/tasks/types/task/InspectionSelection'
import {
    translateInspectionType,
    translateInspectionTypeName,
} from 'aos-services/src/services/tasks/types/task/InspectionType'
import { TaskCategory } from 'aos-services/src/services/tasks/types/TaskCategory'
import { allowedSubcategories } from 'aos-services/src/services/tasks/types/TaskConfig'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import {
    Box,
    EditableList,
    EditableListItemProps,
    HStack,
    Input,
    InputField,
    MultiSelect,
    Text,
} from 'aos-ui-universal'
import type { TFunction } from 'i18next'
import React, { FC, useMemo, useState } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../TaskFormField'
import { InspectionTypeIconRenderer } from './partials'

interface InspectionTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const InspectionTaskForm: FC<InspectionTaskFormProps> = ({ metadata, isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const { control, setValue } = useFormContext<InspectionTaskFormPayload>()
    const watchedItems = useWatch({ control, name: 'items' })
    const items = useMemo(() => watchedItems ?? [], [watchedItems])
    const titleField = useController({ control, name: 'title' })
    const [taskId, parentTaskId] = useWatch({ control, name: ['id', 'parentTaskId'] })
    const isExistingTask = taskId !== undefined || parentTaskId !== undefined

    const [inspectionGroup, setInspectionGroup] = useState<InspectionGroup>()

    const groupConfigs = useMemo(
        () => inspectionTypeToGroups(metadata.inspectionTypes, items),
        [metadata.inspectionTypes, items],
    )
    const groups = (Object.keys(groupConfigs) as InspectionGroup[]).filter(q =>
        allowedSubcategories[TaskCategory.Inspection].includes(q),
    )

    const itemsById = useMemo(
        () =>
            new Map(
                Object.values(groupConfigs)
                    .flatMap(g => g.items)
                    .map(i => [String(i.inspectionType.id), i] as const),
            ),
        [groupConfigs],
    )

    const changeGroupSelection = (group: InspectionGroup, selected: InspectionItemPayload[]) => {
        const nextItems = diffSelection(selected, items, group)
        const titleItem = selected[0] ?? nextItems[0]
        setValue('items', nextItems, { shouldDirty: true, shouldValidate: true })
        titleField.field.onChange(
            titleItem ? translateInspectionTypeName(translate)(titleItem.inspectionType) : '',
        )
    }

    const selectGroup = (group: InspectionGroup) => {
        if (group === inspectionGroup) {
            return
        }
        setInspectionGroup(group)
        const config = groupConfigs[group]
        if (!isExistingTask && !config.selectedItems.length && config.items.length) {
            changeGroupSelection(group, config.items)
        }
    }

    const activeConfig = inspectionGroup ? groupConfigs[inspectionGroup] : undefined

    return (
        <Box className='shrink-0 pb-3'>
            <Controller
                control={control}
                name='items'
                render={({ field, fieldState }) => (
                    <>
                        <TaskFormField
                            required={!isTaskTemplate}
                            error={
                                fieldState.error
                                    ? translate('form.required-field', { ns: 'common' })
                                    : undefined
                            }
                            label={translate('form.inspection.items.label', { ns: 'tasks' })}
                        >
                            <div className='grid grid-cols-4 gap-2'>
                                {groups.map(group => (
                                    <InspectionTypeIconRenderer
                                        key={group}
                                        isSelected={inspectionGroup === group}
                                        onSelect={() => selectGroup(group)}
                                        group={group}
                                        selectedInspectionsCounter={
                                            groupConfigs[group].selectedItems.length
                                        }
                                        inspectionTotalNumber={groupConfigs[group].items.length}
                                    />
                                ))}
                            </div>
                        </TaskFormField>

                        {inspectionGroup && !!activeConfig?.items.length && (
                            <Box className='pt-2'>
                                <MultiSelect
                                    size='md'
                                    options={activeConfig.items.map(i => ({
                                        value: String(i.inspectionType.id),
                                        label: translateInspectionTypeName(translate)(
                                            i.inspectionType,
                                        ),
                                    }))}
                                    value={activeConfig.selectedItems.map(i =>
                                        String(i.inspectionType.id),
                                    )}
                                    summary={selected =>
                                        selected.length === activeConfig.items.length
                                            ? translate('all', {
                                                  items: translateInspectionGroup(translate)(
                                                      inspectionGroup,
                                                  ),
                                                  ns: 'common',
                                              })
                                            : translatePlural(translate)(
                                                  'form.inspection.selected-count',
                                                  selected.length,
                                              )
                                    }
                                    onChange={ids =>
                                        changeGroupSelection(
                                            inspectionGroup,
                                            ids
                                                .map(id => itemsById.get(id))
                                                .filter((i): i is InspectionItemPayload => !!i),
                                        )
                                    }
                                />
                            </Box>
                        )}

                        {!!field.value?.length && (
                            <Box className='shrink-0 pt-4'>
                                <EditableList
                                    value={field.value}
                                    itemRenderer={props => (
                                        <SelectedItemRenderer {...props} translate={translate} />
                                    )}
                                    onChange={field.onChange}
                                />
                            </Box>
                        )}
                    </>
                )}
            />
        </Box>
    )
}
type SelectedItemRendererProps = EditableListItemProps<InspectionItemPayload> & {
    translate: TFunction
}

export const SelectedItemRenderer: FC<SelectedItemRendererProps> = ({
    item,
    onChangeItem,
    translate,
}) => {
    return (
        <HStack className='items-center gap-5 pb-3 pr-5'>
            <Box className='flex-1'>
                <Text size='body' tone='muted' numberOfLines={1}>
                    {translateInspectionType(translate)(item.inspectionType)}
                </Text>
            </Box>
            <Box className='flex-1'>
                <Input>
                    <InputField
                        placeholder={translate('form.inspection.comment.placeholder', {
                            ns: 'tasks',
                        })}
                        value={item.comment ?? ''}
                        onChangeText={comment => onChangeItem({ ...item, comment })}
                    />
                </Input>
            </Box>
        </HStack>
    )
}
